import React, { useEffect, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';

import Chart from 'chart.js/auto';
import 'chartjs-adapter-date-fns';
import zoomPlugin from 'chartjs-plugin-zoom';
import { DATA_RANGE, DATASET_TYPES, DATASET_CONFIG } from '@awp/constants';

import {
  buildGraphData,
  buildOptions,
  getRegularDatasets,
  getGraphMargin,
  getImageDatasets,
  getMultiWXFrostProbeDatasets,
} from '../Common/Helper';

import {
  addOrSubtractDaysToTimestamp,
  addOrSubtractHoursToTimestamp,
  daysBetweenEpochsRoundedUp,
  midpointBetweenEpochs,
  roundDateDownToHour,
} from '@awp/helpers/Utils';

import { TemperatureProfileController, TemperatureProfileElement } from '@graphs/HeatMap/temperatureProfile';

import {
  selectMultiStationData,
  getSelectedDatasetsForGraph,
  selectPrecisionResistorObservation,
} from '@stores/dataVisualizationSlice';

import { useMultiStationContext } from '../MultiStation/MultiStationContext';
import ImageTimelineBlock from '../Image/TimelineBlock';
import { selectAllWeatherStations } from '@stores/weatherStationSlice';
import { isValueEmpty } from '@awp/helpers/utilsReact';
import { toast } from 'react-toastify';
import { ReticleService } from '@graphs/Common/ReticleService';

// Get weather station ids including deferred wsId from a graph preset
// obj is the dataGraph object containing PersistentGraphFilters
// wsId is the deferred weather station id
const getWeatherStationIdsFromGraph = (obj, wsId) => {
  // If obj is null or undefined, return null
  if (!obj) return null;
  // Use a Set to store unique WeatherStationId values to avoid duplicates
  const uniqueWeatherStationIds = new Set();

  // Check if PersistentGraphFilters exists and is an array
  if (obj.PersistentGraphFilters && Array.isArray(obj.PersistentGraphFilters)) {
    // Iterate through each filter in the PersistentGraphFilters array
    obj.PersistentGraphFilters.forEach((filter) => {
      // If WeatherStationId is null, use wsId, otherwise use the actual WeatherStationId
      const _weatherStationId = filter.WeatherStationId === null ? wsId : filter.WeatherStationId;

      // Only add valid (non-null) IDs to the Set
      if (_weatherStationId !== null && _weatherStationId != -1) {
        uniqueWeatherStationIds.add(_weatherStationId);
      }
    });
  }

  // Convert the Set to an Array and return it
  return Array.from(uniqueWeatherStationIds);
};

// Registering Chart.js plugins and controllers
Chart.register(zoomPlugin, TemperatureProfileController, TemperatureProfileElement);

/**
 * Renders a dynamic graph component capable of displaying various datasets with zoom and pan features.
 * Utilizes Chart.js for rendering the graphs.
 *
 * @param {Object} props - Component properties.
 * @param {Object} props.dataGraph - Configuration and data specifics for the graph to be rendered.
 * @param {Array} props.selectedOptions - Active selection criteria that influence graph rendering.
 * @param {boolean} props.isForecastGraph - If the parent component is a weatherNet forecast(true) either in a data table component or multi-station graph component.
 * @param {boolean} props.isSingleStation - If the parent component is embeded in a data table component (true) or in a multi-station graph component(false).
 * @param {Number} props.singleStationId - Selected weather station id in a data table component (isSingleStation must be true)
 * @param {Object} props.singleDataRange - DataRange in a data table component (isSingleStation must be true)
 * @returns {React.Component} The Graph component.
 */
const Graph = (props) => {
  const { dataGraph, selectedOptions, isForecastGraph, hourlyEnd, isSingleStation, singleStationId, singleDataRange } =
    props;

  const {
    showInvalid,
    showReticle,
    timelineValues,
    setTimelineValues,
    dataRange,
    updateDataRange,
    weatherStationId,
    heatMapObservation,
    hasDeferWeatherStation,
    forecastWeatherStationId,
    lastClickedXValue,
    lastHoveredXValue,
    setLastClickedXValue,
    setLastHoveredXValue,
  } = isSingleStation
    ? {
        showInvalid: false,
        timelineValues: null,
        setTimelineValues: null,
        dataRange: singleDataRange,
        updateDataRange: null,
        weatherStationId: singleStationId,
        heatMapObservation: null,
        hasDeferWeatherStation: false,
        forecastWeatherStationId: singleStationId,
      }
    : useMultiStationContext();

  const graphId = dataGraph.DataGraphId;
  const selectedDatasetOptions = selectedOptions;

  const multiStationData = useSelector(selectMultiStationData);
  const allWeatherStations = useSelector(selectAllWeatherStations);

  // get weather station ids including defer ws id from a graph from a preset
  const wsIds = getWeatherStationIdsFromGraph(dataGraph, weatherStationId);
  const wsIdSet = !wsIds ? null : new Set(wsIds);
  // Filter WeatherStationIds by wsIds
  const filteredWeatherStationIds = !wsIdSet
    ? multiStationData?.WeatherStationIds
    : multiStationData?.WeatherStationIds.filter((id) => wsIdSet.has(id));
  // Filter DataVisualizationDTOs based on WeatherStationId in wsIds
  const filteredDataVisualizationDTOs = !wsIdSet
    ? multiStationData?.DataVisualizationDTOs
    : multiStationData?.DataVisualizationDTOs?.filter((dv) => wsIdSet.has(dv.WeatherStationId));

  const filteredDatasets = filteredDataVisualizationDTOs
    .flatMap((dv) => dv.Datasets)
    ?.filter(
      (fds) =>
        (fds.type !== DATASET_TYPES.ForecastData && fds.sensorTypeName !== DATASET_CONFIG.WxNetPavement) ||
        (fds.type === DATASET_TYPES.ForecastData &&
          fds.sensorTypeName === DATASET_CONFIG.WxNetPavement &&
          forecastWeatherStationId > 0 &&
          fds.weatherStationId === forecastWeatherStationId),
    );

  const data = {
    ...multiStationData,
    WeatherStationIds: filteredWeatherStationIds,
    DataVisualizationDTOs: filteredDataVisualizationDTOs,
    Datasets: filteredDatasets,
  };
  const showHeatMap = isSingleStation
    ? false
    : selectedDatasetOptions.find((d) => d.typeId === heatMapObservation.ObservationTypeId) ?? false;

  const precisionResistorObservation = useSelector(selectPrecisionResistorObservation);
  const selectedDatasets = getSelectedDatasetsForGraph(
    selectedDatasetOptions,
    data,
    heatMapObservation,
    allWeatherStations,
  );
  const regularDatasets = getRegularDatasets(selectedDatasets, precisionResistorObservation);
  const imageDatasets = getImageDatasets(selectedDatasets);

  const frostProbeDatasets = showHeatMap
    ? getMultiWXFrostProbeDatasets(multiStationData, selectedDatasetOptions, heatMapObservation, dataRange)
    : null;

  const [graphData, setGraphData] = useState([]);
  const [graphOptions, setGraphOptions] = useState([]);

  let zoomDebounceTimer = null;

  /**
   * Debounces zoom-related updates to prevent excessive recalculations and re-renders.
   *
   * @param {Function} func - The function to execute after the specified delay.
   * @param {number} delay - Time in milliseconds to wait before executing the function.
   * @param {boolean} immediate - If true, triggers the function immediately on the leading edge.
   * @param {Chart} chart - The Chart.js instance associated with the event.
   * @param {Object} params - Parameters to pass to the function when invoked.
   */
  const debounceZoomComplete = (func, delay, immediate, chart, params) => {
    const callNow = immediate && !zoomDebounceTimer;

    clearTimeout(zoomDebounceTimer);

    zoomDebounceTimer = setTimeout(() => {
      if (!callNow) {
        func(chart, params);
      }
      zoomDebounceTimer = null;
    }, delay);

    if (callNow) {
      func(chart, params);
    }
  };

  /**
   * Finalizes zoom adjustments by updating chart scales and internal state.
   *
   * @param {Chart} chart - Chart.js instance.
   * @param {Object} zoomDetails - Details including new minimum and maximum values.
   */
  const zoomComplete = (chart, { min, max, daysDiff }) => {
    if (chart.canvas?.parentNode) {
      const tooltip = chart.canvas.parentNode.querySelector('div');
      tooltip.style.opacity = 0;
    }

    updateDataRange(max, daysDiff);

    setTimelineValues({
      min: min,
      max: max,
      left: getGraphMargin(chart.scales, 'left'),
      right: getGraphMargin(chart.scales, 'right'),
    });

    chart.scales.xAxis.min = min;
    chart.scales.xAxis.max = max;
  };

  /**
   * Handles zoom events triggered from user interactions by recalculating necessary parameters
   * and initiating the debounce process.
   *
   * @param {Object} event - Event object containing the chart instance.
   */
  const handleZoomEvent = ({ chart }) => {
    let { min, max } = chart.scales.xAxis;
    let daysDiff = daysBetweenEpochsRoundedUp(min, max);
    const midPoint = midpointBetweenEpochs(min, max);

    let immediate = false;

    if (daysDiff > DATA_RANGE.MAXIMUM) {
      // When daysDiff exceeds the maximum allowable range,
      // we set daysDiff to the maximum and adjust min and max around the endpoint.
      daysDiff = DATA_RANGE.MAXIMUM;
      max = dataRange.endDate; // Assuming this is a fixed endpoint
      min = addOrSubtractDaysToTimestamp(max, -daysDiff);
      immediate = true; // Execute immediately if daysDiff is at its MAXIMUM
    } else {
      // Adjust min and max symmetrically around the midpoint
      min = addOrSubtractDaysToTimestamp(midPoint, -(daysDiff / 2));
      max = addOrSubtractDaysToTimestamp(midPoint, daysDiff / 2);
    }

    debounceZoomComplete(zoomComplete, 500, immediate, chart, { min, max, daysDiff });
  };

  /**
   * Handles the completion of panning on the chart, updating the timeline values and data range.
   *
   * @param {object} event - The event object provided by Chart.js.
   */
  const panComplete = ({ chart }) => {
    let { min, max } = chart.scales.xAxis;

    // hide any showing tooltips
    if (chart.canvas?.parentNode) {
      const tooltip = chart.canvas.parentNode.querySelector('div');
      tooltip.style.opacity = 0;
    }

    setTimelineValues({
      min: min,
      max: max,
      left: getGraphMargin(chart.scales, 'left'),
      right: getGraphMargin(chart.scales, 'right'),
    });

    updateDataRange(max, dataRange.displayRange);
  };

  /**
   * Memoizes the component's configuration and prepares datasets for rendering.
   * This setup is recalculated when key inputs such as selected options or data range change.
   */
  useMemo(() => {
    if (selectedDatasetOptions.length == 0) {
      let chart = Chart.getChart(graphId.toString());

      if (chart) {
        chart.data.datasets = []; //clear the chart
        //clearing the scales except x axis
        graphOptions.scales = Object.keys(graphOptions.scales)
          .filter((d) => (!d.startsWith('yAxis') ? d : undefined))
          .reduce((r, k) => ((r[k] = graphOptions.scales[k]), r), {});
        setGraphOptions(graphOptions);

        chart.update();
      }

      //exit out
      return;
    }

    if (regularDatasets == null) return;
    //need to generate the start date here becasue if it was updated by a dispatch
    // that dispatch may not have been completed yet
    let min = isForecastGraph
      ? addOrSubtractHoursToTimestamp(hourlyEnd, -30)
      : addOrSubtractDaysToTimestamp(dataRange.endDate, -dataRange.displayRange); //setStartDate(dataRange.endDate, dataRange.displayRange);
    let datasets = buildGraphData(regularDatasets, showInvalid, isForecastGraph);

    let options = isForecastGraph
      ? {
          ...buildOptions(
            regularDatasets,
            graphId,
            min,
            hourlyEnd,
            panComplete,
            handleZoomEvent,
            showInvalid,
            true,
            showReticle,
            lastClickedXValue,
            lastHoveredXValue,
            setLastClickedXValue,
            setLastHoveredXValue,
          ),
          groupByWeatherStation: true,
        }
      : {
          ...buildOptions(
            regularDatasets,
            graphId,
            roundDateDownToHour(min),
            dataRange.endDate,
            panComplete,
            handleZoomEvent,
            showInvalid,
            false,
            showReticle,
            lastClickedXValue,
            lastHoveredXValue,
            setLastClickedXValue,
            setLastHoveredXValue,
          ),
          groupByWeatherStation: true,
        };

    let fpObs = {};
    let depths = [];

    if (frostProbeDatasets?.length > 0) {
      //get the first one
      const weatherStationInfo = frostProbeDatasets[0].weatherStationInfo;

      Object.keys(frostProbeDatasets).map((k) => {
        if (frostProbeDatasets[k].Data) {
          Object.keys(frostProbeDatasets[k].Data).map((d) => {
            const ts = parseFloat(frostProbeDatasets[k].Data[d].x);
            fpObs[ts] = fpObs[ts] ?? [];

            fpObs[ts].push({
              displayFormat: frostProbeDatasets[k].graphConfig.displayFormat,
              isNan: Number.isNaN(frostProbeDatasets[k].Data[d].Value) || frostProbeDatasets[k].Data[d].Value === 'NAN',
              isMissing: !frostProbeDatasets[k].Data[d].Value,
              value: parseFloat(frostProbeDatasets[k].Data[d].Value),
              isValid: !frostProbeDatasets[k].Data[d].IsInvalid,
              elevation: frostProbeDatasets[k].Data[d].Depth / 10,
            });
          });
          depths.push(frostProbeDatasets[k].rawElevation / 10);
        }
      });

      depths.sort((a, b) => b - a);

      let heatMapObs = [];
      Object.keys(fpObs).map((o) => {
        heatMapObs.push([o, fpObs[o]]);
      });

      //filter the data down to the visible range
      // heatMapObs = heatMapObs
      //   .map((f) => {
      //     if (parseFloat(f[0]) > min && parseFloat(f[0]) < dataRange.endDate) {
      //       return f;
      //     }
      //   })
      //   .filter((e) => e !== undefined);

      datasets.push({
        type: 'TemperatureProfile',
        label: 'Frost Probe',
        displayFormat:
          frostProbeDatasets.map((f) => f.graphConfig.displayFormat).filter((v, i, s) => s.indexOf(v) === i)[0] || '',
        collectionInterval:
          frostProbeDatasets.map((f) => f.collectionIntervalMinutes).filter((v, i, s) => s.indexOf(v) === i)[0] || 60,
        legendColor: frostProbeDatasets.map((f) => f.color).filter((v, i, s) => s.indexOf(v) === i)[0] || '',
        unitLabel: frostProbeDatasets.map((f) => f.unitLabel).filter((v, i, s) => s.indexOf(v) === i)[0] || '',
        data: heatMapObs,
        yAxisID: 'yAxis-FP',
        xAxisID: 'xAxis',
        order: Infinity, // Must draw last or it will cover the other datasets
        datasetName: 'Frost Probe',
        yAxisList: null,
        weatherStationInfo: weatherStationInfo,
      });

      options.scales = {
        ...options.scales,
        'yAxis-FP': {
          position: 'right',
          min: depths[depths.length - 1],
          max: depths[0],
          thermistorDepths: depths,
          showInvalidData: showInvalid,
          grid: { drawOnChartArea: false },
          title: {
            padding: {
              bottom: 36,
            },
            text: 'Frost Probe (cm)',
            display: true,
          },
        },
      };
    }
    setGraphData(datasets);
    setGraphOptions(options);
  }, [dataRange, showInvalid, selectedDatasetOptions, showReticle, lastClickedXValue, lastHoveredXValue]);

  /**
   * Effect hook for initializing and updating the chart based on current state.
   */
  useEffect(() => {
    let chart = null;
    if (!graphOptions || graphData.length === 0) return;

    const graphElm = document.getElementById(graphId);
    if (graphElm !== null) {
      const ctx = graphElm.getContext('2d');
      chart = Chart.getChart(graphId.toString());

      if (chart) {
        try {
          chart.data.datasets = graphData;
          chart.options = graphOptions;
          chart.update();
        } catch (error) {
          console.error(error);
        }
      } else {
        graphOptions.scales.xAxis.min = isForecastGraph
          ? Math.floor(addOrSubtractHoursToTimestamp(hourlyEnd, -30))
          : Math.floor(addOrSubtractDaysToTimestamp(dataRange.endDate, -dataRange.displayRange));
        graphOptions.scales.xAxis.max = isForecastGraph ? Math.floor(hourlyEnd) : Math.floor(dataRange.endDate);

        try {
          chart = new Chart(ctx, {
            data: {
              datasets: graphData,
            },
            options: graphOptions,
            plugins: [
              {
                beforeDraw: (chart) => {
                  const FPAxis = chart.scales['yAxis-FP'];
                  if (FPAxis) {
                    let width = 96;
                    let height = 12;

                    let x = Math.round(FPAxis.right) - 20;
                    let y = Math.round(FPAxis.top + FPAxis.bottom - width) / 2;

                    let tickStart = height - 4;
                    let tickEnd = height + 4;

                    //let points = [1, 50, 95];
                    let linePoints = [16, 48, 80];
                    let textPoints = [linePoints[0] - 12, linePoints[1] - 8, linePoints[2] - 12];

                    var ctx = chart.ctx;
                    ctx.save();

                    ctx.translate(x, y);
                    ctx.rotate(Math.PI / 2);

                    var gradient = ctx.createLinearGradient(0, 0, width, 0);
                    gradient.addColorStop(0, '#FEE918');
                    gradient.addColorStop(0.2, '#FEE918');
                    gradient.addColorStop(0.5, '#37B47B');
                    gradient.addColorStop(0.8, '#460056');
                    gradient.addColorStop(1, '#460056');
                    ctx.fillStyle = gradient;
                    ctx.fillRect(0, 0, width, height);

                    ctx.beginPath();
                    ctx.moveTo(0, height);
                    ctx.lineTo(width - 2, height);
                    ctx.stroke();

                    linePoints.map((point) => {
                      ctx.beginPath();
                      ctx.moveTo(point, tickStart);
                      ctx.lineTo(point, tickEnd);
                      ctx.stroke();
                    });

                    ctx.fillStyle = 'black';
                    ctx.font = 'bold 10px Arial';
                    ctx.fillText('+1°C', textPoints[0], 28);
                    ctx.fillText('0°C', textPoints[1], 28);
                    ctx.fillText('-1°C', textPoints[2], 28);
                    ctx.restore();
                  }

                  ReticleService.drawReticle(chart);
                },
              },
            ],
          });
        } catch (error) {
          // Handle missing Graph Types
          const filteredGraphData = graphData.filter((x) => {
            const hasNoType = isValueEmpty(x.type);
            if (hasNoType) {
              toast.error(`No Graph Type selected for ${x.datasetName} (${x.label})`, { autoClose: false });
            }
            return hasNoType;
          });

          // Handle error when creating the chart
          if (filteredGraphData.length === 0) {
            toast.error(error?.message, { autoClose: 5000 });
          }

          console.error(error);
        }
      }
    }

    if (!isSingleStation && chart)
      setTimelineValues({
        min: chart.scales.xAxis.min,
        max: chart.scales.xAxis.max,
        left: getGraphMargin(chart.scales, 'left'),
        right: getGraphMargin(chart.scales, 'right'),
      });
  }, [graphData, graphOptions]);

  return (
    <>
      {graphData.length > 0 ? (
        <>
          <div style={{ height: isForecastGraph ? '400px' : '200px' }}>
            <canvas
              id={graphId}
              key={graphId}
              data={{
                datasets: graphData,
              }}
            />
          </div>
        </>
      ) : null}
      {imageDatasets.length > 0 ? (
        <div>
          <ImageTimelineBlock
            imageDatasets={getImageDatasets(imageDatasets)}
            graphId={graphId}
            timelineValues={timelineValues}
          />
        </div>
      ) : null}
    </>
  );
};

export default Graph;
